import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import saveAs from "file-saver";
import { getQueryParams } from "@/utils/httpRequest";
import { formatFileSize } from "@/utils/fileUtils";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import { showNotification } from "@/redux/actions/notification";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";

export const lenderHubLoanSummaryService = createApi({
  reducerPath: "lenderHubLoanSummaryApi",
  tagTypes: ["LenderHubLoanSummary", "LenderHubLoanSummaryNeedListFile"],
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/capworks/",
    credentials: "include",
  }),
  endpoints: builder => ({
    getLoanSummaryById: builder.query({
      query: ({ dealId, loanSummaryId }) => `deals/${dealId}/summaries/${loanSummaryId}`,
      providesTags: (result, error, { loanSummaryId }) => [{ type: "LenderHubLoanSummary", id: loanSummaryId }],
    }),
    getLoanSummaryNeedListFiles: builder.query({
      query: ({ dealId, loanSummaryId, ...params }) =>
        `deals/${dealId}/summaries/${loanSummaryId}/needs-list?${getQueryParams(params)}`,
      providesTags: [{ type: "LenderHubLoanSummaryNeedListFile", id: "LIST" }],
      transformResponse: items => {
        // First, group the items by borrower and document
        const grouped = items.reduce((item, file) => {
          const records = { ...item };
          if (!item[file.borrowerId]) {
            records[file.borrowerId] = {
              borrowerFullName: file.borrowerFullName,
              documents: {
                [file.needListId]: {
                  documentName: file.documentName,
                  files: [],
                },
              },
            };
          }

          if (!records[file.borrowerId].documents[file.needListId]) {
            records[file.borrowerId].documents[file.needListId] = {
              documentName: file.documentName,
              files: [],
            };
          }

          records[file.borrowerId].documents[file.needListId].files.push({
            loanId: file.loanId,
            loanSummaryId: file.loanSummaryId,
            borrowerId: file.borrowerId,
            needListId: file.needListId,
            needListFileId: file.needListFileId,
            fileName: file.fileName,
            fileSize: file.fileSize,
            fileType: file.contentType,
            receivedAt: file.receivedAt,
            sentAt: file.sentAt,
            isActive: file.isActive,
          });

          return records;
        }, {});

        // Transform grouped data into tree format
        const treeNodes = [];

        Object.entries(grouped).forEach(([borrowerId, borrowerData]) => {
          const borrowerNode = {
            id: borrowerId,
            text: borrowerData.borrowerFullName,
            hasChildren: true,
            iconCss: "user",
            child: [],
            type: "borrower",
            payload: {
              ...borrowerData,
            },
          };

          Object.entries(borrowerData.documents).forEach(([needListId, documentData]) => {
            const documentNode = {
              id: needListId,
              text: documentData.documentName,
              hasChildren: documentData.files.length > 0,
              iconCss: "folder",
              child: [],
              type: "document",
              payload: {
                ...documentData,
              },
            };

            documentData.files.forEach(file => {
              const fileNode = {
                id: file.needListFileId,
                text: `${file.fileName}${file.fileSize ? ` (${formatFileSize(file.fileSize)})` : ""}`,
                hasChildren: false,
                iconCss: "document",
                type: "file",
                isChecked: file.isActive,
                payload: {
                  ...file,
                },
              };

              documentNode.child.push(fileNode);
            });

            borrowerNode.child.push(documentNode);
          });

          treeNodes.push(borrowerNode);
        });

        return treeNodes;
      },
    }),
    previewLoanSummaryNeedListFile: builder.mutation({
      query: ({ dealId, loanSummaryId, borrowerId, needListId, needListFileId }) => ({
        url: `deals/${dealId}/summaries/${loanSummaryId}/needs-list/${needListId}/borrowers/${borrowerId}/files/${needListFileId}/preview`,
      }),
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_LOAN_SUMMARY_NEED_LIST_FILE_PREVIEWED, data.file);

          // Open preview in new tab using the URL from response
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.previewUrl;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File download started successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    downloadLoanSummaryNeedListFile: builder.mutation({
      query: ({ dealId, loanSummaryId, borrowerId, needListId, needListFileId }) => ({
        url: `deals/${dealId}/summaries/${loanSummaryId}/needs-list/${needListId}/borrowers/${borrowerId}/files/${needListFileId}/preview`,
      }),
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_HUB_LOAN_SUMMARY_NEED_LIST_FILE_DOWNLOADED, data.file);

          // Trigger download using the URL from response
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.downloadUrl;
          a.download = data.file.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File download started successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    downloadLoanSummaryNeedListPackage: builder.mutation({
      queryFn: async ({ dealId, loanSummaryId }, api, extraOptions, baseQuery) => {
        try {
          api.dispatch(setProcessingState(true));
          const result = await baseQuery({
            url: `deals/${dealId}/summaries/${loanSummaryId}/needs-list/package`,
            responseHandler: response => {
              if (!response.ok) {
                return response.json();
              }

              return response.blob();
            },
          });

          if (result?.data) {
            saveAs(result.data, "Loan Package.zip");

            api.dispatch(
              showNotification({
                ...DEFAULT_NOTIFICATION_PAYLOAD,
                message: {
                  ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                  description: "Package generated successfully! The file has been automatically saved to your device.",
                  options: {
                    variant: NOTIFICATION_VARIANTS.SUCCESS,
                  },
                },
              }),
            );
          }

          api.dispatch(setProcessingState(false));
          return { data: result?.data, error: result?.error };
        } catch (err) {
          api.dispatch(setProcessingState(false));
          Bugsnag.notify(err);
          return { error: err };
        }
      },
    }),
  }),
});

export const {
  useGetLoanSummaryByIdQuery,
  useGetLoanSummaryNeedListFilesQuery,
  usePreviewLoanSummaryNeedListFileMutation,
  useDownloadLoanSummaryNeedListFileMutation,
  useDownloadLoanSummaryNeedListPackageMutation,
} = lenderHubLoanSummaryService;
